// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — ActivityNode: Rounded rectangle 90×38
// M2: Fuente uniforme T.font.nodeTitle, sin descripción
// M4: Edición inline con doble clic (data.editing)
// M8: NodeResizer controlado por data.resizeEnabled
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { ActivityNodeData, FlowNodeData } from '../../types/flowchart.types';

function ActivityNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as ActivityNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  // Sincronizar valor al entrar en edición
  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Prevent React Flow from capturing keys (Backspace, Delete, etc.)
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={60}
        minHeight={30}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      {/* 4 lados — target + source en cada uno */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        viewBox={`0 0 ${T.node.width} ${T.node.height}`}
      >
        <rect
          x={T.shape.strokeWidth / 2}
          y={T.shape.strokeWidth / 2}
          width={T.node.width - T.shape.strokeWidth}
          height={T.node.height - T.shape.strokeWidth}
          rx={T.shape.borderRadius}
          ry={T.shape.borderRadius}
          fill={T.color.nodeFill}
          stroke={selected ? T.color.nodeStrokeSelected : T.color.nodeStroke}
          strokeWidth={selected ? T.shape.strokeWidthSelected : T.shape.strokeWidth}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px 4px',
          fontFamily: T.font.family,
          boxShadow: T.shape.shadow,
          borderRadius: T.shape.borderRadius,
          pointerEvents: editing ? 'auto' : 'none',
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.textPrimary,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.textPrimary,
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>

      {/* Request count badge (viewer mode) */}
      {!editing && (d as any).requestCount > 0 && (
        <div
          title={`${(d as any).requestCount} solicitudes vinculadas`}
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: T.color.secondary,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            fontFamily: T.font.family,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            pointerEvents: 'auto',
            zIndex: 5,
          }}
        >
          {(d as any).requestCount}
        </div>
      )}
    </div>
  );
}

export const ActivityNode = memo(ActivityNodeComponent);
